import type { FC } from 'react';
import { useContext, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import ConfigContext from 'app/common/ConfigContext';
import { getEBattleSubdivisionLabels, isEbattle } from 'app/config/videogameSpecific.config.helper';
import MatchesRecap from 'app/components/MatchesRecap';
import SlidingContent from 'app/components/SlidingContent';
import TeamName from 'app/components/TeamName';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';
import FlagIcon from 'app/icons/flag.svg';

import HeadToHeadEncounter from './HeadToHeadEncounter';
import { getEncountersWins } from './utils';
import style from './style.module.scss';

interface Props {
  data: HeadToHeadBlockMatch;
}

const HeadToHeadBlock: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const { lang } = useContext(ConfigContext);

  const opponent1 = data.opponents.at(0);
  const opponent2 = data.opponents.at(1);

  const opponent1Wins = opponent1 ? getEncountersWins(data.encounters, opponent1.id) : 0;
  const opponent2Wins = opponent2 ? getEncountersWins(data.encounters, opponent2.id) : 0;

  const [selectedEncounter, setSelectedEncounter] = useState<null | Encounter>(null);

  const hasSelectedEncounter = Boolean(selectedEncounter);

  const encounterSlug = data.encounters.at(0)?.videogame_slug;
  const isEBattle = encounterSlug ? isEbattle(encounterSlug) : false;
  const isETennis = encounterSlug === 'e-tennis';
  const isEBattleSub = isEBattle && !isETennis;
  const ebattleLabels =
    isEBattleSub && encounterSlug
      ? getEBattleSubdivisionLabels(encounterSlug as EBattleSlug, lang)
      : null;

  const contentStyle =
    isEBattleSub && ebattleLabels
      ? ({ '--sub-count': ebattleLabels.length } as React.CSSProperties)
      : undefined;

  const winsScore = (
    <div className={style.wins}>
      <span className={classNames({ [style.lowestWinsNb]: opponent1Wins < opponent2Wins })}>
        {opponent1Wins}
      </span>
      <span className={style.winsSeperator}>:</span>
      <span className={classNames({ [style.lowestWinsNb]: opponent2Wins < opponent1Wins })}>
        {opponent2Wins}
      </span>
    </div>
  );

  return (
    <BlockTemplate
      title={t('blocks_head-to-head_title')}
      backButtonCallback={hasSelectedEncounter ? () => setSelectedEncounter(null) : undefined}
      hasData={Boolean(data.encounters?.length)}
    >
      <SlidingContent.Container sliding={hasSelectedEncounter}>
        <SlidingContent aria-hidden={hasSelectedEncounter}>
          <div className={style.headToHeadBlockContent} style={contentStyle}>
            <TeamName.Container
              className={classNames(style.teamsNames, { [style.ebattleTeamsNames]: isEBattle })}
            >
              <TeamName
                team={opponent1}
                logoStyle={classNames(style.teamNameLogoSize, {
                  [style.hiddenLogo]: isEBattle
                })}
                namePosition={isEBattle ? 'side' : 'bottom'}
              />
              <div>
                <div className={style.vs}>{t('all-versus', 'vs')}</div>
                {!isEBattle && winsScore}
              </div>
              <TeamName
                team={opponent2}
                logoStyle={classNames(style.teamNameLogoSize, {
                  [style.hiddenLogo]: isEBattle
                })}
                namePosition={isEBattle ? 'side' : 'bottom'}
                orientation="reversed"
              />
            </TeamName.Container>

            {isEBattle && <div className={style.ebattleWins}>{winsScore}</div>}

            {isEBattleSub && ebattleLabels && (
              <div className={style.ebattleColumnHeaders}>
                <span />
                {ebattleLabels.map((label, i) => (
                  <span
                    key={label}
                    className={classNames(style.ebattleColumnLabel, {
                      [style.ebattleColumnLabelWithSep]: i > 0
                    })}
                  >
                    {label}
                  </span>
                ))}
                <FlagIcon className={style.ebattleFlagIcon} />
              </div>
            )}

            <ul className={classNames(style.lines, { [style.ebattleLines]: isEBattle })}>
              {data.encounters.map(encounter => (
                <li className={style.line} key={encounter.id}>
                  <HeadToHeadEncounter
                    encounter={encounter}
                    onSelect={setSelectedEncounter}
                    opponent1Id={opponent1?.id}
                    opponent2Id={opponent2?.id}
                    selectable={!hasSelectedEncounter}
                  />
                </li>
              ))}
            </ul>
          </div>
        </SlidingContent>

        <SlidingContent>
          {selectedEncounter && (
            <MatchesRecap
              encounter={selectedEncounter}
              opponent1={opponent1}
              opponent2={opponent2}
            />
          )}
        </SlidingContent>
      </SlidingContent.Container>
    </BlockTemplate>
  );
};

HeadToHeadBlock.displayName = 'HeadToHeadBlock';

export default HeadToHeadBlock;
